import { colors } from '../config/colors';

// API Cost Configuration (per request)
const API_COSTS = {
  // Auth Service
  'auth/login': { reads: 0, writes: 1, cost: 0.001 },
  'auth/signup': { reads: 0, writes: 1, cost: 0.001 },
  'auth/health': { reads: 1, writes: 0, cost: 0.0001 },
  
  // Company Service
  'companies/': { reads: 1, writes: 0, cost: 0.0005 },
  'companies/search': { reads: 2, writes: 0, cost: 0.001 },
  'companies/statistics': { reads: 3, writes: 0, cost: 0.0015 },
  'companies/industry': { reads: 2, writes: 0, cost: 0.001 },
  'companies/risk-category': { reads: 2, writes: 0, cost: 0.001 },
  'companies/details': { reads: 5, writes: 0, cost: 0.0025 },
  'companies/risk-summary': { reads: 3, writes: 0, cost: 0.0015 },
  'companies/risk-analysis': { reads: 4, writes: 0, cost: 0.002 },
  'companies/news': { reads: 3, writes: 0, cost: 0.0015 },
  'companies/financials': { reads: 4, writes: 0, cost: 0.002 },
  'companies/supply-chain': { reads: 3, writes: 0, cost: 0.0015 },

  // Risk Analysis Service
  'risk-analysis/analyze': { reads: 2, writes: 1, cost: 0.002 },

  // Watchlist Service
  'watchlist/': { reads: 1, writes: 0, cost: 0.0005 },
  'watchlist/add': { reads: 1, writes: 1, cost: 0.0015 },
  'watchlist/remove': { reads: 0, writes: 1, cost: 0.001 },
  'watchlist/update': { reads: 1, writes: 1, cost: 0.0015 },
  'watchlist/items': { reads: 2, writes: 0, cost: 0.001 },
  'watchlist/statistics': { reads: 3, writes: 0, cost: 0.0015 },
  'watchlist/addresses': { reads: 2, writes: 0, cost: 0.001 },
  'watchlist/routes': { reads: 2, writes: 0, cost: 0.001 },
  'watchlist/with-companies': { reads: 4, writes: 0, cost: 0.002 },
  
  // News Service (from NEWS_API_REFERENCE.md)
  'news/': { reads: 2, writes: 0, cost: 0.001 },
  'news/ticker': { reads: 3, writes: 0, cost: 0.0015 },
  'news/company': { reads: 3, writes: 0, cost: 0.0015 },
  'news/search': { reads: 4, writes: 0, cost: 0.002 },
  'news/fetch': { reads: 5, writes: 1, cost: 0.003 },
  'news/statistics': { reads: 2, writes: 0, cost: 0.001 },
  'news/date-range': { reads: 3, writes: 0, cost: 0.0015 },
  'news/relevant': { reads: 3, writes: 0, cost: 0.0015 },
  'news/risk-category': { reads: 3, writes: 0, cost: 0.0015 }
};

class ApiUsageTracker {
  constructor() {
    this.usageData = this.loadFromStorage();
    this.sessionStartTime = Date.now();
  }

  // Load usage data from localStorage
  loadFromStorage() {
    try {
      const stored = localStorage.getItem('apiUsageData');
      return stored ? JSON.parse(stored) : {
        totalRequests: 0,
        totalReads: 0,
        totalWrites: 0,
        totalCost: 0,
        endpointStats: {},
        dailyStats: {},
        sessionStats: {
          requests: 0,
          reads: 0,
          writes: 0,
          cost: 0,
          startTime: Date.now()
        }
      };
    } catch (error) {
      return this.getDefaultUsageData();
    }
  }

  // Save usage data to localStorage
  saveToStorage() {
    try {
      localStorage.setItem('apiUsageData', JSON.stringify(this.usageData));
    } catch (error) {
    }
  }

  // Get default usage data structure
  getDefaultUsageData() {
    return {
      totalRequests: 0,
      totalReads: 0,
      totalWrites: 0,
      totalCost: 0,
      endpointStats: {},
      dailyStats: {},
      sessionStats: {
        requests: 0,
        reads: 0,
        writes: 0,
        cost: 0,
        startTime: Date.now()
      }
    };
  }

  // Extract endpoint key from URL
  extractEndpointKey(url, method) {
    try {
      // Remove base URL and extract endpoint path
      let endpoint = url.replace(/^https?:\/\/[^\/]+/, '');
      endpoint = endpoint.replace(/^\/api\/v?\d*\//, '');
      endpoint = endpoint.replace(/\/\d+$/, ''); // Remove trailing IDs
      endpoint = endpoint.replace(/\/[a-f0-9-]+$/, ''); // Remove UUIDs
      endpoint = endpoint.replace(/\?.*$/, ''); // Remove query params
      endpoint = endpoint.replace(/\/$/, ''); // Remove trailing slash
      
      // Create method-specific key
      const methodPrefix = method.toLowerCase();
      return `${methodPrefix}/${endpoint}`;
    } catch (error) {
      return 'unknown/unknown';
    }
  }

  // Get cost configuration for endpoint
  getEndpointCost(endpointKey) {
    // Try exact match first
    if (API_COSTS[endpointKey]) {
      return API_COSTS[endpointKey];
    }

    // Try partial matches for dynamic endpoints
    for (const [key, cost] of Object.entries(API_COSTS)) {
      if (endpointKey.includes(key) || key.includes(endpointKey.split('/')[1])) {
        return cost;
      }
    }

    // Default cost for unknown endpoints
    return { reads: 1, writes: 0, cost: 0.0005 };
  }

  // Track API call
  trackApiCall(url, method, responseTime, success = true) {
    const timestamp = Date.now();
    const endpointKey = this.extractEndpointKey(url, method);
    const costConfig = this.getEndpointCost(endpointKey);
    
    // Calculate costs
    const reads = costConfig.reads;
    const writes = costConfig.writes;
    const cost = costConfig.cost;

    // Update total stats
    this.usageData.totalRequests++;
    this.usageData.totalReads += reads;
    this.usageData.totalWrites += writes;
    this.usageData.totalCost += cost;

    // Update session stats
    this.usageData.sessionStats.requests++;
    this.usageData.sessionStats.reads += reads;
    this.usageData.sessionStats.writes += writes;
    this.usageData.sessionStats.cost += cost;

    // Update endpoint-specific stats
    if (!this.usageData.endpointStats[endpointKey]) {
      this.usageData.endpointStats[endpointKey] = {
        requests: 0,
        reads: 0,
        writes: 0,
        cost: 0,
        avgResponseTime: 0,
        successCount: 0,
        errorCount: 0,
        lastCalled: timestamp
      };
    }

    const endpointStat = this.usageData.endpointStats[endpointKey];
    endpointStat.requests++;
    endpointStat.reads += reads;
    endpointStat.writes += writes;
    endpointStat.cost += cost;
    endpointStat.lastCalled = timestamp;

    // Update response time average
    endpointStat.avgResponseTime = 
      (endpointStat.avgResponseTime * (endpointStat.requests - 1) + responseTime) / endpointStat.requests;

    // Update success/error counts
    if (success) {
      endpointStat.successCount++;
    } else {
      endpointStat.errorCount++;
    }

    // Update daily stats
    const today = new Date().toISOString().split('T')[0];
    if (!this.usageData.dailyStats[today]) {
      this.usageData.dailyStats[today] = {
        requests: 0,
        reads: 0,
        writes: 0,
        cost: 0
      };
    }

    this.usageData.dailyStats[today].requests++;
    this.usageData.dailyStats[today].reads += reads;
    this.usageData.dailyStats[today].writes += writes;
    this.usageData.dailyStats[today].cost += cost;

    // Save to storage
    this.saveToStorage();

    // Log the API call
    this.logApiCall(endpointKey, method, reads, writes, cost, responseTime, success);
  }

  logApiCall() {
    // Intentionally no-op: avoid logging API usage details to the browser console.
  }

  // Get color for HTTP method
  getMethodColor(method) {
    switch (method.toLowerCase()) {
      case 'get': return colors.blue;
      case 'post': return colors.green;
      case 'put': return colors.orange;
      case 'delete': return colors.red;
      case 'patch': return colors.purple;
      default: return colors.gray;
    }
  }

  // Get usage statistics
  getUsageStats() {
    const sessionDuration = Date.now() - this.usageData.sessionStats.startTime;
    const sessionDurationMinutes = Math.max(1, sessionDuration / (1000 * 60));
    
    return {
      total: {
        requests: this.usageData.totalRequests,
        reads: this.usageData.totalReads,
        writes: this.usageData.totalWrites,
        cost: this.usageData.totalCost
      },
      session: {
        ...this.usageData.sessionStats,
        duration: sessionDuration,
        durationMinutes: sessionDurationMinutes,
        requestsPerMinute: this.usageData.sessionStats.requests / sessionDurationMinutes,
        costPerMinute: this.usageData.sessionStats.cost / sessionDurationMinutes
      },
      endpoints: this.usageData.endpointStats,
      daily: this.usageData.dailyStats
    };
  }

  // Get top endpoints by usage
  getTopEndpoints(limit = 10) {
    return Object.entries(this.usageData.endpointStats)
      .sort(([,a], [,b]) => b.requests - a.requests)
      .slice(0, limit)
      .map(([endpoint, stats]) => ({
        endpoint,
        ...stats
      }));
  }

  // Get daily usage for the last N days
  getDailyUsage(days = 7) {
    const result = [];
    const today = new Date();
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      result.push({
        date: dateStr,
        ...(this.usageData.dailyStats[dateStr] || {
          requests: 0,
          reads: 0,
          writes: 0,
          cost: 0
        })
      });
    }
    
    return result;
  }

  // Reset usage data
  resetUsageData() {
    this.usageData = this.getDefaultUsageData();
    this.sessionStartTime = Date.now();
    this.saveToStorage();
  }

  // Clear old daily data (keep last 30 days)
  cleanupOldData() {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - 30);
    const cutoffStr = cutoffDate.toISOString().split('T')[0];
    
    Object.keys(this.usageData.dailyStats).forEach(date => {
      if (date < cutoffStr) {
        delete this.usageData.dailyStats[date];
      }
    });
    
    this.saveToStorage();
  }
}

// Create singleton instance
const apiUsageTracker = new ApiUsageTracker();

// Cleanup old data on initialization
apiUsageTracker.cleanupOldData();

export default apiUsageTracker;
